import { fetchApi } from '@inmagik/react-crud'
import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import dayjs from 'dayjs'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useMemo, useRef, useState } from 'react'
import { Button, Card, CardBody, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import ModalConfirm from '../../components/ModalConfirm'
import { API_URL } from '../../constants'
import { useAvvisiContext } from '../../contexts/AvvisiContext'
import { usePartialUpdateBilancia } from '../../hooks/bilance'
import {
  useCreateAllegatoIntervento,
  useDeleteAllegatoIntervento,
  useInterventoDettaglioCampiMancanti,
  usePartialUpdateIntervento,
} from '../../hooks/interventi'
import { AllegatoIntervento, Intervento, PaginatedDJResponse } from '../../types'
import { transformDownloadUrl, useIsAmministratore, useIsCliente, useIsSupervisore } from '../../utils/utils'

interface InterventoFlussoVerificazionePeriodica {
  id: Intervento['id']
  data_intervento: Intervento['data_intervento']
  stato: Intervento['stato']
  bilancia_data?: Intervento['bilancia_data']
  bilancia: Intervento['bilancia']
  foto_targa: Intervento['foto_targa']
  esito_xlsx?: Intervento['esito_xlsx']
  data_foto_targa: Intervento['data_foto_targa']
  data_esito_xlsx: Intervento['data_esito_xlsx']
  esito: Intervento['esito']
  esito_riesame?: Intervento['esito_riesame']
  note_intervento_invalido?: Intervento['note_intervento_invalido']
  numero_pratica_esportazione?: Intervento['numero_pratica_esportazione']
  data_invio?: Intervento['data_invio']
}

type CardFlussoVerificazionePeriodicaProps = {
  intervento: UseQueryResult<InterventoFlussoVerificazionePeriodica, Error>
  allegatiIntervento: UseQueryResult<PaginatedDJResponse<AllegatoIntervento>, Error>
  modalActionsModificaRiesame?: {
    open: (v?: any) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
  modalActionsConfirm?: {
    open: (v?: any) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
}

export default function CardFlussoVerificazionePeriodica({
  intervento,
  allegatiIntervento,
  modalActionsModificaRiesame,
  modalActionsConfirm,
}: CardFlussoVerificazionePeriodicaProps) {
  const isCliente = useIsCliente()
  const { user, tokens } = useAuth()

  const isSupervisore = useIsSupervisore()
  const isAmministratore = useIsAmministratore()

  const fotoTargaInputRef = useRef<HTMLInputElement>(null)
  const esitoInterventoInputRef = useRef<HTMLInputElement>(null)
  const allegatiInputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync: updatePartialIntervento } = usePartialUpdateIntervento()
  const { mutateAsync: createAllegatoIntervento } = useCreateAllegatoIntervento()
  const { mutateAsync: deleteAllegatoIntervento } = useDeleteAllegatoIntervento()

  const [isLoadingEsito, setIsLoadingEsito] = useState(false)
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)
  const [isDownloadingCertificato, setIsDownloadingCertificato] = useState(false)

  const [isSendingCertificato, setIsSendingCertificato] = useState(false)

  const isBilanciaWithoutTipoBilanciaEm = useMemo(() => {
    if (!intervento.data?.bilancia_data?.tipo_bilancia_em) {
      return true
    } else {
      return false
    }
  }, [intervento.data?.bilancia_data?.tipo_bilancia_em])

  const { mutateAsync: updatePartialBilanica } = usePartialUpdateBilancia()
  const [tipoBilanciaEm, setTipoBilanciaEm] = useState('')

  const erroriCampiMancanti = useInterventoDettaglioCampiMancanti(
    String(intervento.data?.id),
    {},
    {
      enabled: !isCliente
    }
  )

  const [modalStateInviaMail, modalActionsInviaMail] = useModalTrigger()

  const blockForCampiMancanti = useMemo(() => {
    if (erroriCampiMancanti?.data && erroriCampiMancanti?.data?.length > 0) {
      return true
    }
    return false
  }, [erroriCampiMancanti])

  const canModifyAttachments = useMemo(() => {
    if (intervento.data?.stato === 'programmato') {
      return true
    }

    return intervento.data?.stato === 'eseguito' && (isSupervisore || isAmministratore)
  }, [intervento.data?.stato, isAmministratore, isSupervisore])

  const { refetch: refetchAvvisi } = useAvvisiContext()

  return (
    <Card>
      <CardBody>
        <h5 className="heading-h5">Verificazione della macchina</h5>
        {!isCliente &&
          erroriCampiMancanti?.data &&
          erroriCampiMancanti?.data?.length > 0 &&
          intervento.data?.stato !== 'trasmesso' &&
          intervento.data?.stato !== 'esportato' &&
          intervento.data?.stato !== 'annullato' &&
          intervento.data?.stato !== 'non_eseguito' && (
            <div className="row mt-2">
              <div className="col-12">
                <div className="border border-danger p-3">
                  <div className="text-danger">
                    Mancano dei campi obbligatori per poter continuare con la verificazione. Aggiorna i campi mancanti
                    per poter procedere.
                  </div>
                  <div className="mt-3 text-black">
                    <div className="label-s">Campi mancanti</div>
                    <div className="p-md mt-2">
                      {erroriCampiMancanti.data?.map((campo) => (
                        <div key={campo}>{campo}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        {!isCliente && isBilanciaWithoutTipoBilanciaEm && (
          <div className="border border-danger p-3 mt-3">
            <div className="text-danger">
              La bilancia non ha un tipo di bilancia (Elettronica/Meccanica) impostato. Aggiorna il tipo di bilancia per
              poter procedere con la verificazione.
            </div>

            <select
              className="form-select my-3"
              value={tipoBilanciaEm}
              onChange={(e) => setTipoBilanciaEm(e.target.value)}
            >
              <option value="">Seleziona un tipo di bilancia</option>
              <option value="pesa_elettronica">Pesa elettronica</option>
              <option value="pesa_meccanica">Pesa meccanica</option>
            </select>
            <Button
              variant="secondary"
              disabled={!tipoBilanciaEm}
              className="text-white d-flex align-items-center"
              onClick={async () => {
                await updatePartialBilanica({
                  id: intervento.data?.bilancia as number,
                  tipo_bilancia_em: tipoBilanciaEm,
                }).then(() => {
                  toast.success('Tipo bilancia impostato con successo', {
                    position: 'bottom-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                  })
                  intervento.refetch()
                })
              }}
            >
              Salva
            </Button>
          </div>
        )}
        {!isCliente && (
          <div className="d-flex align-items-center justify-content-between my-4">
            <span className="p-md">Scarica l’excel pre-compilato per effettuare la verificazione.</span>
            <Button
              variant="secondary"
              disabled={
                intervento?.data?.stato === 'eseguito' ||
                intervento.data?.stato === 'annullato' ||
                intervento.data?.stato === 'richiesto' ||
                isDownloadingExcel ||
                isBilanciaWithoutTipoBilanciaEm ||
                blockForCampiMancanti
              }
              onClick={async () => {
                try {
                  setIsDownloadingExcel(true)
                  const data = await fetchApi(
                    `${API_URL}/bilance/intervento/${intervento?.data?.id}/download-excel-intervento-periodico/`,
                    {
                      method: 'GET',

                      headers: {
                        Authorization: 'Bearer ' + tokens?.access,
                      },
                    }
                  )
                  const blob = await data.blob()
                  const contentDisposition = data.headers.get('Content-Disposition')
                  const idIntervento = intervento?.data?.id
                  const dataIntervento = dayjs(intervento?.data?.data_intervento).format('DD-MM-YYYY')
                  const filename = contentDisposition
                    ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                    : `verificazione-periodica-${idIntervento}-${dataIntervento}.xlsx`
                  const url = window.URL.createObjectURL(new Blob([blob]))
                  const link = document.createElement('a')
                  link.href = url
                  link.setAttribute('download', filename)
                  document.body.appendChild(link)
                  link.click()
                  link.parentNode?.removeChild(link)
                } catch (e) {
                  toast.error("Errore nel download dell'excel", {
                    position: 'bottom-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                  })
                } finally {
                  setIsDownloadingExcel(false)
                }
              }}
              className="text-white d-flex align-items-center"
            >
              <span
                className="material-icons me-2"
                style={{
                  fontSize: '16px',
                }}
              >
                download
              </span>
              Scarica excel
              {isDownloadingExcel && <Spinner animation="border" variant="light" className="ms-4" size="sm" />}
            </Button>
          </div>
        )}
        <div>
          <div className="heading-h6">Allegati</div>
          <div className="mt-4">
            <div className="d-flex label-m-bold d-flex align-items-center">
              <span className="material-icons me-2">photo_camera</span>
              <span>Foto targa</span>
            </div>
            {!isCliente && (
              <div className="mt-2">Inserisci una foto della targa della macchina sotto verificazione.</div>
            )}
            <div className="mt-3 d-flex align-items-center">
              {!isCliente && !intervento?.data?.foto_targa && (
                <>
                  <Button
                    variant="outline-secondary"
                    className="text-secondary d-flex align-items-center"
                    disabled={
                      !canModifyAttachments ||
                      intervento?.data?.stato === 'annullato' ||
                      isBilanciaWithoutTipoBilanciaEm ||
                      blockForCampiMancanti
                    }
                    onClick={() => {
                      fotoTargaInputRef.current?.click()
                    }}
                  >
                    <span className="material-icons">attach_file</span>
                  </Button>
                  <span className="ms-2">Inserisci allegato</span>
                </>
              )}
              {intervento?.data?.foto_targa && (
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <a href={transformDownloadUrl(intervento?.data?.foto_targa as string)} target="_blank">
                        <img
                          src={
                            typeof intervento?.data?.foto_targa === 'string'
                              ? transformDownloadUrl(intervento?.data?.foto_targa)
                              : URL.createObjectURL(intervento?.data?.foto_targa)
                          }
                          className="me-4"
                          alt="Foto targa"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                          }}
                        />
                      </a>
                      <div>
                        <span className="p-md">
                          {/* name of the file from the path */}
                          {typeof intervento?.data?.foto_targa === 'string'
                            ? intervento?.data?.foto_targa.split('/').pop()?.split('?')[0]
                            : intervento?.data?.foto_targa.name.split('?')[0]}
                        </span>
                        <div className="p-sm text-body-secondary">
                          {intervento?.data?.data_foto_targa && (
                            <span>
                              Upload effettuato il {dayjs(intervento?.data?.data_foto_targa).format('DD/MM/YYYY')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      {canModifyAttachments && (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="text-white d-flex align-items-center me-2"
                          onClick={() => {
                            fotoTargaInputRef.current?.click()
                          }}
                        >
                          <span
                            className="material-icons"
                            style={{
                              fontSize: 20,
                            }}
                          >
                            upload
                          </span>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="d-flex align-items-center"
                        onClick={() => {
                          typeof intervento?.data?.foto_targa === 'string' && window.open(intervento?.data?.foto_targa)
                        }}
                      >
                        <span
                          className="material-icons me-2"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          download
                        </span>
                        Scarica
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {!isCliente && (
                <input
                  ref={fotoTargaInputRef}
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      if (!canModifyAttachments) {
                        toast.error("L'intervento non è più modificabile. Ricarico lo stato aggiornato.", {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        intervento.refetch()
                        e.target.value = ''
                        return
                      }

                      try {
                        await updatePartialIntervento({
                          id: intervento?.data?.id,
                          foto_targa: e.target.files[0],
                          data_foto_targa: dayjs().format('YYYY-MM-DD'),
                        })
                          .then(() => {
                            toast.success('Foto targa aggiunta con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            refetchAvvisi()
                          })
                          .finally(() => {
                            e.target.value = ''
                          })
                      } catch (e) {
                        toast.error('Errore durante il caricamento della foto', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        intervento.refetch()
                      }
                    }
                  }}
                />
              )}
            </div>
          </div>
          {!isCliente && (
            <div className="mt-4">
              <div className="d-flex label-m-bold d-flex align-items-center">
                <span className="material-icons me-2">description</span>
                <span>Esito intervento</span>
              </div>
              <div className="mt-2">Dopo aver scaricato l’excel , ricaricalo qui compilato e firmato.</div>
              <div className="mt-3 d-flex align-items-center">
                {!intervento?.data?.esito_xlsx ? (
                  <>
                    <Button
                      variant="outline-secondary"
                      className="text-secondary d-flex align-items-center"
                      disabled={
                        !canModifyAttachments ||
                        intervento?.data?.stato === 'annullato' ||
                        isBilanciaWithoutTipoBilanciaEm ||
                        blockForCampiMancanti
                      }
                      onClick={() => {
                        esitoInterventoInputRef.current?.click()
                      }}
                    >
                      <span className="material-icons">attach_file</span>
                    </Button>
                    <span className="ms-2">Inserisci allegato</span>
                  </>
                ) : (
                  <div className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <span className="material-icons text-secondary me-2 bg-secondary-light p-2 rounded">
                          description
                        </span>
                        <div>
                          <span className="p-md">
                            {/* name of the file from the path */}
                            {typeof intervento?.data?.esito_xlsx === 'string'
                              ? intervento?.data?.esito_xlsx.split('/').pop()?.split('?')[0]
                              : intervento?.data?.esito_xlsx.name.split('?')[0]}
                          </span>
                          <div className="p-sm text-body-secondary">
                            {intervento?.data?.data_esito_xlsx && (
                              <span>
                                Upload effettuato il {dayjs(intervento?.data?.data_esito_xlsx).format('DD/MM/YYYY')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        {canModifyAttachments && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-white d-flex align-items-center me-2"
                            onClick={() => {
                              esitoInterventoInputRef.current?.click()
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{
                                fontSize: 20,
                              }}
                            >
                              upload
                            </span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="d-flex align-items-center"
                          as="a"
                          target="_blank"
                          href={transformDownloadUrl(intervento?.data?.esito_xlsx as string)}
                        >
                          <span
                            className="material-icons me-2"
                            style={{
                              fontSize: 16,
                            }}
                          >
                            download
                          </span>
                          Scarica
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <input
                  ref={esitoInterventoInputRef}
                  type="file"
                  className="d-none"
                  accept=".xltm, .xlsm, .xlsx"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      if (!canModifyAttachments) {
                        toast.error("L'intervento non è più modificabile. Ricarico lo stato aggiornato.", {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        intervento.refetch()
                        e.target.value = ''
                        return
                      }

                      try {
                        await updatePartialIntervento({
                          id: intervento?.data?.id,
                          esito_xlsx: e.target.files[0],
                          data_esito_xlsx: dayjs().format('YYYY-MM-DD'),
                          utente: user?.id,
                        })
                          .then(async () => {
                            toast.success('Esito intervento aggiunto con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            setIsLoadingEsito(true)
                            await fetchApi(`${API_URL}/bilance/intervento/${intervento?.data?.id}/esito-intervento/`, {
                              method: 'GET',

                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + tokens?.access,
                              },
                            }).then((res) => {
                              setIsLoadingEsito(false)
                              intervento.refetch()
                              refetchAvvisi()
                            })
                          })
                          .finally(() => {
                            e.target.value = ''
                            setIsLoadingEsito(false)
                          })
                      } catch (e) {
                        toast.error('Errore durante il caricamento dell’esito', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        intervento.refetch()
                      } finally {
                        setIsLoadingEsito(false)
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
          <div className="mt-4">
            <div className="d-flex label-m-bold d-flex align-items-center">
              <span>Altri allegati</span>
            </div>
            {!isCliente && <div className="mt-2">Inserisci qui qualsiasi altro documento della verificazione.</div>}
          </div>
          {allegatiIntervento?.data?.results &&
            allegatiIntervento?.data?.results.length === 0 &&
            (user?.role === 'supervisore' || user?.role === 'amministratore') && (
              <div className="mt-3 text-secondary">Nessun allegato presente</div>
            )}
          {allegatiIntervento?.data?.results && allegatiIntervento?.data?.results.length > 0 && (
            <div className="mt-3">
              {allegatiIntervento?.data?.results.map((allegato) => (
                <div key={allegato.id} className="d-flex align-items-center justify-content-between mt-3">
                  <div className="d-flex align-items-center">
                    <span className="material-icons me-2 bg-secondary-light p-2 rounded text-secondary">
                      description
                    </span>
                    <span>{String(allegato.allegato).split('/').pop()?.split('?')[0]}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    {canModifyAttachments && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="d-flex align-items-center me-2"
                        onClick={async () => {
                          await deleteAllegatoIntervento(allegato.id).then(() => {
                            toast.success('Allegato eliminato con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            intervento.refetch()
                            allegatiIntervento.refetch()
                            refetchAvvisi()
                          })
                        }}
                      >
                        <span className="material-icons">delete</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="text-secondary d-flex align-items-center"
                      onClick={() => {
                        window.open(String(allegato.allegato))
                      }}
                    >
                      <span className="material-icons me-2">download</span>
                      Scarica
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {canModifyAttachments && (
            <div className="mt-3 d-flex align-items-center">
              <Button
                variant="outline-secondary"
                className="text-secondary d-flex align-items-center"
                disabled={!canModifyAttachments || isBilanciaWithoutTipoBilanciaEm || blockForCampiMancanti}
                onClick={() => {
                  allegatiInputRef.current?.click()
                }}
              >
                <span className="material-icons">add</span>
              </Button>
              <span className="ms-2">Aggiungi allegato</span>
              <input
                ref={allegatiInputRef}
                type="file"
                className="d-none"
                onChange={async (e) => {
                  if (e.target.files && e.target.files[0] && intervento.data) {
                    if (!canModifyAttachments) {
                      toast.error("L'intervento non è più modificabile. Ricarico lo stato aggiornato.", {
                        position: 'bottom-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      })
                      intervento.refetch()
                      e.target.value = ''
                      return
                    }

                    try {
                      await createAllegatoIntervento({
                        intervento: intervento.data.id,
                        allegato: e.target.files[0],
                      })
                        .then(() => {
                          toast.success('Allegato aggiunto con successo', {
                            position: 'bottom-right',
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                          })
                          intervento.refetch()
                          refetchAvvisi()
                        })
                        .finally(() => {
                          e.target.value = ''
                        })
                    } catch (e) {
                      toast.error('Errore durante il caricamento dell’allegato', {
                        position: 'bottom-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      })
                      intervento.refetch()
                      allegatiIntervento.refetch()
                    }
                  }
                }}
              />
            </div>
          )}

          {intervento?.data?.esito_xlsx && (
            <div className="d-flex align-items-center mt-5 justify-content-between">
              <div className="d-flex align-items-center">
                <div className="heading-h4">Esito certificazione</div>
                <div>
                  {isLoadingEsito && (
                    <div className="ms-4">
                      <Spinner animation="border" variant="secondary" />
                    </div>
                  )}
                  {intervento.data?.esito !== '' && (
                    <div className="d-flex align-items-center ms-4">
                      {intervento.data?.esito === 'positivo' ? (
                        <div className="d-flex align-items-center">
                          <span className="material-icons me-2 text-success">check</span>
                          <span className="heading-h3 text-capitalize">{intervento.data.esito}</span>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center">
                          <span className="material-icons me-2 text-danger">close</span>
                          <span className="heading-h3 text-capitalize">{intervento.data?.esito}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                {!isCliente && intervento.data.stato === 'programmato' && (
                  <Button
                    variant="secondary"
                    disabled={
                      !intervento.data?.esito_xlsx ||
                      !intervento.data?.foto_targa ||
                      allegatiIntervento?.data?.results?.length === 0
                    }
                    className="text-white d-flex align-items-center"
                    onClick={async () => {
                      try {
                        await updatePartialIntervento({
                          id: intervento?.data?.id,
                          stato: 'eseguito',
                        }).then(() => {
                          toast.success('Esito intervento impostato a effettuato', {
                            position: 'bottom-right',
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                          })
                          intervento.refetch()
                          refetchAvvisi()
                        })
                      } catch (e) {
                        toast.error('Errore durante il caricamento dell’esito', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        intervento.refetch()
                      }
                    }}
                  >
                    <span className="material-icons me-2">send</span>
                    Invia al supervisore
                  </Button>
                )}
                {user?.role === 'operatore' &&
                  (intervento.data.stato === 'eseguito' || intervento.data.stato === 'riesaminato') && (
                    <div className="text-success">Inviato con successo!</div>
                  )}
                {intervento.data.stato === 'eseguito' &&
                  !intervento.data.esito_riesame &&
                  (user?.role === 'supervisore' || user?.role === 'amministratore') && (
                    <div className="d-flex align-items-center">
                      <Button
                        variant="danger"
                        className="text-white d-flex align-items-center"
                        onClick={async () => {
                          await updatePartialIntervento({
                            id: intervento?.data?.id,
                            esito_riesame: 'invalido',
                          }).then(() => {
                            toast.error('Esito riesame impostato a invalido', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            intervento.refetch()
                          })
                        }}
                      >
                        <span
                          className="material-icons me-2"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          close
                        </span>
                        Invalida
                      </Button>
                      <Button
                        variant="success"
                        className="text-white d-flex align-items-center ms-2"
                        onClick={async () => {
                          await updatePartialIntervento({
                            id: intervento?.data?.id,
                            esito_riesame: 'conforme',
                          }).then(() => {
                            toast.success('Esito riesame impostato a conforme', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            intervento.refetch()
                          })
                        }}
                      >
                        <span
                          className="material-icons me-2"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          check
                        </span>
                        Convalida
                      </Button>
                    </div>
                  )}
                {(user?.role === 'supervisore' || user?.role === 'amministratore') && intervento.data.esito_riesame && (
                  <div className="d-flex align-items-center">
                    {intervento.data.stato === 'eseguito' && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="text-secondary d-flex align-items-center me-2"
                        onClick={() => {
                          modalActionsModificaRiesame?.open(intervento.data)
                        }}
                      >
                        <i
                          className="material-icons"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          edit
                        </i>
                        Modifica
                      </Button>
                    )}
                    <div
                      className={classNames('d-flex align-items-center p-sm', {
                        'text-success': intervento.data.esito_riesame === 'conforme',
                        'text-danger': intervento.data.esito_riesame === 'invalido',
                      })}
                    >
                      <span className="p-sm text-capitalize">
                        {intervento.data.esito_riesame === 'conforme' ? 'Conforme' : 'Invalido'}
                      </span>
                      <span className="material-icons ms-2" style={{ fontSize: 16 }}>
                        {intervento.data.esito_riesame === 'conforme' ? 'check' : 'close'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {intervento.data?.esito_riesame !== '' &&
            (user?.role === 'supervisore' || user?.role === 'amministratore') && (
              <div className="d-flex align-items-center mt-3 justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="heading-h4">Esito riesame</div>
                  <div className="ms-4">
                    {intervento.data?.esito_riesame !== '' && (
                      <span className="heading-h3 text-capitalize">{intervento.data?.esito_riesame}</span>
                    )}
                  </div>
                </div>
                {intervento.data?.stato !== 'riesaminato' &&
                intervento.data?.stato !== 'esportato' &&
                intervento.data?.stato !== 'trasmesso' ? (
                  <Button
                    variant="secondary"
                    className="text-white d-flex align-items-center"
                    onClick={() => {
                      modalActionsConfirm?.open(intervento.data)
                    }}
                  >
                    Certifica
                  </Button>
                ) : (
                  <div className="d-flex align-items-center">
                    {intervento.data.stato === 'riesaminato' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-white d-flex align-items-center me-2"
                        onClick={async () => {
                          await updatePartialIntervento({
                            id: intervento?.data?.id,
                            stato: 'eseguito',
                          }).then(() => {
                            toast.success('Intervento impostato a eseguito', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                            intervento.refetch()
                          })
                        }}
                      >
                        Annulla certificazione
                      </Button>
                    )}
                    <div
                      className="text-success 
                        d-flex align-items-center p-sm"
                    >
                      Certificato
                      <span
                        className="material-icons ms-2"
                        style={{
                          fontSize: 16,
                        }}
                      >
                        check
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          {!isCliente && intervento.data?.esito_riesame === 'invalido' && intervento.data.stato === 'riesaminato' && (
            <div className="mt-3">
              <div className="label-md">NOTE</div>
              <div className="mt-2">{intervento.data?.note_intervento_invalido}</div>
            </div>
          )}
          {intervento.data?.stato === 'trasmesso' && intervento.data.esito_xlsx && (
            <div className="mt-2">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  {!isCliente && intervento.data.numero_pratica_esportazione && (
                    <div className="badge bg-primary p-2">
                      Numero pratica:{' '}
                      <span className="text-white fw-bold">{intervento.data.numero_pratica_esportazione}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Button
                    disabled={isDownloadingCertificato}
                    variant="secondary"
                    className="text-white d-flex align-items-center"
                    onClick={async () => {
                      try {
                        setIsDownloadingCertificato(true)
                        const data = await fetchApi(
                          `${API_URL}/bilance/intervento/${intervento?.data?.id}/download-certificato/`,
                          {
                            method: 'GET',
                            headers: {
                              'Content-Type': 'application/pdf',
                              Authorization: 'Bearer ' + tokens?.access,
                            },
                          }
                        )
                        const blob = await data.blob()
                        const url = window.URL.createObjectURL(new Blob([blob]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute(
                          'download',
                          `certificato-${intervento?.data?.id}-${dayjs(intervento?.data?.data_intervento).format(
                            'DD-MM-YYYY'
                          )}.pdf`
                        )
                        document.body.appendChild(link)
                        link.click()
                        link.parentNode?.removeChild(link)
                      } catch (e) {
                        toast.error('Errore nel download del certificato', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                      } finally {
                        setIsDownloadingCertificato(false)
                      }
                    }}
                  >
                    <MaterialIcon icon="download" className="me-2" />
                    <span>Scarica certificato</span>
                    {isDownloadingCertificato && (
                      <Spinner animation="border" variant="light" className="ms-4" size="sm" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {intervento?.data?.numero_pratica_esportazione && (isAmministratore || isSupervisore) && (
            <div>
              <hr />
              {intervento.data.data_invio && (
                <div>
                  Data ultimo invio: <strong>{dayjs(intervento.data.data_invio).format('DD/MM/YYYY')}</strong>
                </div>
              )}
              <div className="mt-2 d-flex align-items-center justify-content-start">
                <Button
                  variant="outline-secondary"
                  disabled={isSendingCertificato}
                  size="sm"
                  className="text-secondary d-flex align-items-center"
                  onClick={async () => {
                    modalActionsInviaMail.open(intervento.data)
                  }}
                >
                  <span className="material-icons me-2">email</span>
                  <span>{intervento.data.data_invio ? 'Rinvia' : 'Invia'} certificato</span>
                  {isSendingCertificato && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardBody>
      <ModalConfirm
        title="Invia certificato"
        isOpen={modalStateInviaMail.isOpen}
        toggle={modalActionsInviaMail.toggle}
        confirmText="Invia"
        cancelText="Annulla"
        body={`Vuoi ${
          intervento?.data?.data_invio ? 'rinviare' : 'inviare'
        } il certificato al cliente al seguente indirizzo: ${
          intervento?.data?.bilancia_data?.sede_data?.email_referente
        }`}
        action={async () => {
          setIsSendingCertificato(true)
          await fetchApi(`${API_URL}/bilance/intervento/${intervento?.data?.id}/invia-certificato/`, {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + tokens?.access,
            },
          }).then(() => {
            setIsSendingCertificato(false)
            intervento.refetch()
            modalActionsInviaMail.close()
            toast.success('Certificato inviato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          })
        }}
      />
    </Card>
  )
}
